import { IndicatorResume } from './IndicatorResume';
import { IndicatorAreaDatas } from './IndicatorAreaDatas';
import { GraphicAreaData } from './GraphicAreaData';
import { GraphicAreaVolumeData } from './GraphicAreaVolumeData';
import { GraphicLegalBaseVolumeData } from './GraphicLegalBaseVolumeData';
import { GeneralView } from './GeneralView';
import { 
  Box, 
  Paper, 
  Typography, 
  Avatar, 
  Divider 
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GavelIcon from '@mui/icons-material/Gavel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export const ReportsRopaExecutiveSummary = ({ dados }) => {
  const { t } = useTranslation('mapping');
  return (
    <>
      {/* Dados da Empresa */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper 
          elevation={2}
          sx={{ 
            mt: 4, 
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #d4e8f9 0%, #b8d9f7 100%)',
              color: '#3a4a5a'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(58,74,90,0.15)', width: 48, height: 48, mr: 2 }}>
                <BusinessIcon sx={{ fontSize: 28, color: '#6b7999' }} />
              </Avatar>
              <Typography variant="h5" fontWeight="bold">
                {t('ropa.executiveSummary.companyData')}
              </Typography>
            </Box>
          </Box>

          {/* Nome da Empresa */}
          <Box sx={{ p: 3, bgcolor: '#f5f8fe' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#6b7999" gutterBottom>
              {t('ropa.executiveSummary.companyName')}
            </Typography>
            <Typography variant="h6" color="text.primary">
              {dados?.companyName}
            </Typography>
          </Box>

          <Divider />

          {/* Resumo da Empresa */}
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <DescriptionIcon sx={{ mr: 1, color: '#a5b8d4', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold" color="#6b7999">
                {t('ropa.executiveSummary.companyResume')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {dados?.companyDescriptionResume}
            </Typography>
          </Box>
        </Paper>
      </motion.div>

      {/* Sumário Executivo */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Paper 
          elevation={2}
          sx={{ 
            mt: 3, 
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #dce5f9 0%, #c5d6f7 100%)',
              color: '#3a4a5a'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ bgcolor: 'rgba(58,74,90,0.15)', width: 48, height: 48, mr: 2 }}>
                <AssessmentIcon sx={{ fontSize: 28, color: '#6b7999' }} />
              </Avatar>
              <Typography variant="h5" fontWeight="bold">
                {t('ropa.executiveSummary.title')}
              </Typography>
            </Box>
          </Box>

          {/* Indicadores */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="#3a4a5a" sx={{ mb: 2 }}>
              {t('ropa.executiveSummary.indicators')}
            </Typography>
            <IndicatorResume indicatorResume={dados.indicatorResume} />
            <IndicatorAreaDatas indicatorAreaDatas={dados.indicatorAreaDatas} />
          </Box>
        </Paper>
      </motion.div>

      {/* Uso de Dados Pessoais */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Paper 
          elevation={2}
          sx={{ 
            mt: 3, 
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #d4eef9 0%, #b8e5f7 100%)',
              color: '#3a5a6a'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(58,90,106,0.15)', width: 48, height: 48, mr: 2 }}>
                <DataUsageIcon sx={{ fontSize: 28, color: '#6b8a99' }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {t('ropa.executiveSummary.personalDataUseByArea')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            <GraphicAreaData indicatorDataGraphicByArea={dados.indicatorDataGraphicByArea} />
          </Box>
        </Paper>
      </motion.div>

      {/* Volume de Processo e Base Legal */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Paper 
          elevation={2}
          className="page-break-auto"
          sx={{ 
            mt: 3, 
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #dce5f9 0%, #c5d6f7 100%)',
              color: '#3a4a5a'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(58,74,90,0.15)', width: 48, height: 48, mr: 2 }}>
                <AccountTreeIcon sx={{ fontSize: 28, color: '#6b7999' }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {t('ropa.executiveSummary.processVolumeByArea')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            <GraphicAreaVolumeData indicatorVolumeGraphicByArea={dados.indicatorVolumeGraphicByArea} />
          </Box>
        </Paper>
      </motion.div>

      {/* Volume por Base Legal */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Paper 
          elevation={2}
          sx={{ 
            mt: 3, 
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #d4f1e0 0%, #bce8cd 100%)',
              color: '#3a5a4a'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(58,90,74,0.15)', width: 48, height: 48, mr: 2 }}>
                <GavelIcon sx={{ fontSize: 28, color: '#6b9979' }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {t('ropa.executiveSummary.dataAndProcessVolumeByLegalBase')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            <GraphicLegalBaseVolumeData indicatorVolumeGraphicByLegalBase={dados.indicatorVolumeGraphicByLegalBase} />
          </Box>
        </Paper>
      </motion.div>

      {/* Visão Geral */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <Paper 
          elevation={2}
          className="page-break-auto"
          sx={{ 
            mt: 3, 
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #d4e8f9 0%, #b8d9f7 100%)',
              color: '#3a4a5a'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'rgba(58,74,90,0.15)', width: 48, height: 48, mr: 2 }}>
                <VisibilityIcon sx={{ fontSize: 28, color: '#6b7999' }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {t('ropa.executiveSummary.generalView')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            <GeneralView generalView={dados.generalView} />
          </Box>
        </Paper>
      </motion.div>
    </>
  );
};
